import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import useSSE from '../hooks/useSSE';
import { hasPermission } from '../utils/permissions';

const API = API_BASE_URL;

/**
 * Default preset columns that ship with every status report. Mirrors
 * the DEFAULT_COLUMNS list in server/src/routes/statusReport.js so the
 * client can offer one-click restore of any preset column the user
 * has previously removed. Keep this list in sync with the server. The
 * `permission` field gates display when the user lacks the listed
 * permission (currently used only for 'view_compensation').
 */
const DEFAULT_PRESETS = [
  { id: 'code', name: 'Code', type: 'text', width: 80 },
  { id: 'name', name: 'Candidate', type: 'preset', presetKey: 'name', width: 160, locked: true },
  { id: 'company', name: 'Company', type: 'preset', presetKey: 'company', width: 140 },
  { id: 'title', name: 'Title', type: 'preset', presetKey: 'title', width: 140 },
  { id: 'stage', name: 'Interview Round', type: 'preset', presetKey: 'interview_round', width: 130 },
  { id: 'comp', name: 'Comp Expectations', type: 'preset', presetKey: 'comp', width: 140, permission: 'view_compensation' },
  { id: 'location', name: 'Location', type: 'preset', presetKey: 'location', width: 120 },
  { id: 'notice', name: 'Contractual Obligations', type: 'preset', presetKey: 'notice', width: 170 },
  { id: 'notes', name: 'Notes', type: 'text', width: 200 },
];

/**
 * StatusReport — Editable grid view of pipeline candidates.
 * Grouped by custom sections, with preset + custom columns.
 * Designed for screensharing with clients on virtual calls.
 */
const StatusReport = ({ pipelineId, pipelineName, readOnly = false }) => {
  const [config, setConfig] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [firmLogo, setFirmLogo] = useState(null);
  const [firmName, setFirmName] = useState('');
  const [shareLinks, setShareLinks] = useState({}); // { fileId: shareUrl }
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareConfig, setShareConfig] = useState({ expires_hours: 168, password: '' });
  const [generatingLinks, setGeneratingLinks] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [excludedCandidates, setExcludedCandidates] = useState([]);
  const [excludedCount, setExcludedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null); // { candidateId, columnId }
  const [editValue, setEditValue] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [dragCandidate, setDragCandidate] = useState(null);
  const [openFileDropdown, setOpenFileDropdown] = useState(null); // { candidateId, rect }
  // PDF export controls. `pdfOrientation` defaults to landscape because
  // status reports usually have 6+ columns and portrait crams everything.
  // `hiddenInPdf` lets the user temporarily omit specific columns from
  // the export without removing them from the on-screen table — useful
  // for sharing trimmed views with clients. `columnPickerAnchor` carries
  // the dropdown's anchor position so it pops out next to the trigger
  // button instead of stealing screen real estate.
  const [pdfOrientation, setPdfOrientation] = useState('landscape'); // 'landscape' | 'portrait'
  const [hiddenInPdf, setHiddenInPdf] = useState(() => new Set()); // Set<columnId>
  const [columnPickerAnchor, setColumnPickerAnchor] = useState(null); // null | { left, top }
  // Column resize — tracks an active drag (column ID + starting mouse X
  // and starting width). When non-null the global mousemove handler
  // updates that column's width; mouseup clears + persists.
  const [resizingCol, setResizingCol] = useState(null); // null | { id, startX, startW }
  // Recently-removed column tracker — drives the undo toast. Cleared on
  // dismiss/restore/timeout. We snapshot the column AND its index so
  // undo restores in the original position rather than appending.
  const [recentlyRemoved, setRecentlyRemoved] = useState(null); // null | { column, index, timer }
  // Brief highlight on a column header right after it's restored —
  // gives the user clear visual feedback that the undo worked and
  // shows where the column came back.
  const [recentlyRestored, setRecentlyRestored] = useState(null); // null | columnId
  // Drag-and-drop column reorder. `draggedColumnId` is set when the
  // user starts dragging a column header; `dragOverColumnId` tracks
  // the hover target for an inline indicator. Locked columns (Name)
  // can't be dragged. Reorder persists to the server on drop.
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [dragOverColumnId, setDragOverColumnId] = useState(null);
  const fileDropdownRef = useRef(null);
  const [dragOverGroup, setDragOverGroup] = useState(null);
  // When a candidate is dragged into the Disqualified/Withdrawn group, prompt
  // for a reason before committing — mirrors the CPP / interview-stage flow so
  // the status change is deliberate and captured, not a silent side effect.
  const [terminalDrop, setTerminalDrop] = useState(null); // { candidateId, name, groupId }
  const [terminalReason, setTerminalReason] = useState('');
  const [terminalSaving, setTerminalSaving] = useState(false);
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#2563EB');
  const [saving, setSaving] = useState(false);
  const editInputRef = useRef(null);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Load data
  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/pipelines/${pipelineId}/status-report`, { headers });
      setConfig(res.data.config);
      setCandidates(res.data.candidates);
      setFirmLogo(res.data.firm_logo || null);
      setFirmName(res.data.firm_name || '');
      if (res.data.excluded_count > 0) setExcludedCount(res.data.excluded_count);
    } catch (err) {
      console.error('Error loading status report:', err);
    } finally {
      setLoading(false);
    }
  }, [pipelineId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Live updates — reload the status report whenever any candidate-changing
  // event fires on this pipeline. This covers interview-round changes (rating
  // updates), status changes, card moves between stages, feedback adds, and
  // candidate reactivations. Each preset column is recomputed server-side, so
  // reload is the right unit of refresh.
  useSSE(pipelineId, {
    onStatusChanged: () => loadData(),
    onRatingUpdated: () => loadData(),
    onFeedbackAdded: () => loadData(),
    onCardMoved: () => loadData(),
    onMeetingUpdated: () => loadData(),
    onCandidateReactivated: () => loadData(),
  });

  useEffect(() => { if (showExcluded) loadExcluded(); }, [showExcluded]);

  // Column-resize global mouse handlers. Active only while a column is
  // being dragged (`resizingCol` non-null). Move events update the column
  // width in `config` directly so the UI tracks the cursor; mouseup
  // persists the final value to the server and clears the drag state.
  // 40px floor prevents users from accidentally collapsing a column to
  // nothing.
  useEffect(() => {
    if (!resizingCol) return;
    const onMove = (e) => {
      const delta = e.clientX - resizingCol.startX;
      const next = Math.max(40, resizingCol.startW + delta);
      setConfig(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          columns: prev.columns.map(c =>
            c.id === resizingCol.id ? { ...c, width: next } : c
          )
        };
      });
    };
    const onUp = async () => {
      const finalCols = (config?.columns || []).map(c => ({ ...c }));
      setResizingCol(null);
      try {
        await axios.put(
          `${API}/api/pipelines/${pipelineId}/status-report/config`,
          { columns: finalCols },
          { headers }
        );
      } catch (err) { console.error('Failed to persist column width:', err); }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [resizingCol, config, pipelineId]);

  // Group candidates
  const grouped = useMemo(() => {
    if (!config) return {};
    const map = {};
    for (const g of config.groups) {
      map[g.id] = [];
    }
    // Add ungrouped bucket
    map['_ungrouped'] = [];
    for (const c of candidates) {
      const gid = c.group_id && map[c.group_id] !== undefined ? c.group_id : '_ungrouped';
      map[gid].push(c);
    }
    // Sort within each group by position
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => (a.position ?? 999) - (b.position ?? 999));
    }
    return map;
  }, [config, candidates]);

  // Get cell value (preset or custom) — cell_data overrides preset for editable presets
  const getCellValue = (candidate, column) => {
    if (column.type === 'preset' && column.presetKey) {
      // Allow cell_data to override preset for editable preset columns (location, comp, etc.)
      const override = candidate.cell_data?.[column.id];
      if (override !== undefined && override !== null && override !== '') return override;
      return candidate._presets?.[column.presetKey] || '';
    }
    return candidate.cell_data?.[column.id] || '';
  };

  // Whether a cell currently has a manual override (cell_data) shadowing the
  // live preset value. Used by the edit UI to show a "Use live data" affordance
  // when the user is editing a cell that has a stale manual edit.
  const hasOverride = (candidate, column) => {
    if (column.type !== 'preset' || !column.presetKey) return false;
    const v = candidate.cell_data?.[column.id];
    return v !== undefined && v !== null && v !== '';
  };

  // Save cell edit. An empty/blank value DELETES the override (so the cell
  // falls back to the live preset) rather than storing an empty string.
  const saveCell = async (candidateId, columnId, value) => {
    const candidate = candidates.find(c => c.candidate_id === candidateId);
    if (!candidate) return;

    const newCellData = { ...(candidate.cell_data || {}) };
    const trimmed = (value || '').trim();
    if (trimmed === '') {
      delete newCellData[columnId];
    } else {
      newCellData[columnId] = value;
    }

    // Optimistic update
    setCandidates(prev => prev.map(c =>
      c.candidate_id === candidateId ? { ...c, cell_data: newCellData } : c
    ));

    try {
      await axios.put(
        `${API}/api/pipelines/${pipelineId}/status-report/candidate/${candidateId}`,
        { cell_data: newCellData },
        { headers }
      );
    } catch (err) {
      console.error('Error saving cell:', err);
    }
  };

  // Start editing a cell
  const startEdit = (candidateId, columnId, currentValue) => {
    if (readOnly) return;
    setEditingCell({ candidateId, columnId });
    setEditValue(currentValue || '');
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  // Commit edit
  const commitEdit = () => {
    if (editingCell) {
      const col = config.columns.find(c => c.id === editingCell.columnId);
      // Only save non-preset or unlocked preset columns. Exception: the
      // `name` preset is locked (so its column can't be removed from the
      // report) but IS user-editable — cell_data override shadows the
      // candidate's real name for display in this report. Without this
      // carve-out, typing in the name cell would silently discard on
      // commit because saveCell is gated by the unlocked check below.
      const isNameOverride = col?.type === 'preset' && col?.presetKey === 'name';
      if (col && (col.type !== 'preset' || !col.locked || isNameOverride)) {
        saveCell(editingCell.candidateId, editingCell.columnId, editValue);
      }
    }
    setEditingCell(null);
    setEditValue('');
  };

  // Drag & drop between groups
  const handleDragStart = (e, candidate) => {
    if (readOnly) { e.preventDefault(); return; }
    setDragCandidate(candidate);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', candidate.candidate_id);
  };

  const handleDragOver = (e, groupId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverGroup(groupId);
  };

  const handleDrop = async (e, targetGroupId) => {
    e.preventDefault();
    setDragOverGroup(null);
    if (!dragCandidate || dragCandidate.group_id === targetGroupId) {
      setDragCandidate(null);
      return;
    }

    const candidateId = dragCandidate.candidate_id;
    const candidateName = dragCandidate.cell_data?.name || dragCandidate.name || 'this candidate';

    // Dropping into a terminal group changes the candidate's real status, so
    // prompt for a reason first rather than committing silently. The actual
    // save happens in commitTerminalDrop.
    if (targetGroupId === 'disqualified' || targetGroupId === 'withdrawn') {
      setTerminalReason('');
      setTerminalDrop({ candidateId, name: candidateName, groupId: targetGroupId });
      setDragCandidate(null);
      return;
    }

    // Optimistic update
    setCandidates(prev => prev.map(c =>
      c.candidate_id === candidateId ? { ...c, group_id: targetGroupId } : c
    ));
    setDragCandidate(null);

    try {
      await axios.put(
        `${API}/api/pipelines/${pipelineId}/status-report/candidate/${candidateId}`,
        { group_id: targetGroupId },
        { headers }
      );
    } catch (err) {
      console.error('Error moving candidate:', err);
      loadData(); // revert
    }
  };

  // Commit a disqualify/withdraw drop with the captured reason. This moves the
  // candidate into the terminal group AND (server-side) sets their real status,
  // moves the card to the disqualified stage, and syncs the search pool — so
  // the status report can't drift from every other surface.
  const commitTerminalDrop = async () => {
    if (!terminalDrop) return;
    setTerminalSaving(true);
    const { candidateId, groupId } = terminalDrop;
    // Optimistic update
    setCandidates(prev => prev.map(c =>
      c.candidate_id === candidateId ? { ...c, group_id: groupId } : c
    ));
    try {
      await axios.put(
        `${API}/api/pipelines/${pipelineId}/status-report/candidate/${candidateId}`,
        { group_id: groupId, rejection_reason: terminalReason.trim() || null },
        { headers }
      );
      setTerminalDrop(null);
      setTerminalReason('');
      loadData(); // pull back the synced status/stage
    } catch (err) {
      console.error('Error applying status:', err);
      loadData(); // revert
    } finally {
      setTerminalSaving(false);
    }
  };

  const handleDragEnd = () => {
    setDragCandidate(null);
    setDragOverGroup(null);
  };

  // Toggle group collapse
  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  };

  // Add custom column
  const addColumn = async () => {
    if (!newColumnName.trim()) return;
    const id = 'custom_' + Date.now();
    const newCol = { id, name: newColumnName.trim(), type: 'text', width: 150 };
    const updated = [...config.columns, newCol];
    setConfig(prev => ({ ...prev, columns: updated }));
    setShowAddColumn(false);
    setNewColumnName('');

    try {
      await axios.put(
        `${API}/api/pipelines/${pipelineId}/status-report/config`,
        { columns: updated },
        { headers }
      );
    } catch (err) { console.error(err); }
  };

  // Remove column. Captures the column + its original index in
  // `recentlyRemoved` so the user can undo the removal from the toast
  // that appears at the bottom of the panel for the next 8 seconds.
  // Locked columns (e.g. the Name preset) can't be removed.
  const removeColumn = async (colId) => {
    const index = config.columns.findIndex(c => c.id === colId);
    if (index < 0) return;
    const col = config.columns[index];
    if (col?.locked) return;
    const updated = config.columns.filter(c => c.id !== colId);
    setConfig(prev => ({ ...prev, columns: updated }));

    // Clear any pending undo timer (rapid successive removes — only
    // the most recent removal is undoable, matching common UX patterns
    // like Gmail's "Undo" toast).
    if (recentlyRemoved?.timer) clearTimeout(recentlyRemoved.timer);
    const timer = setTimeout(() => setRecentlyRemoved(null), 8000);
    setRecentlyRemoved({ column: col, index, timer });

    try {
      await axios.put(
        `${API}/api/pipelines/${pipelineId}/status-report/config`,
        { columns: updated },
        { headers }
      );
    } catch (err) { console.error(err); }
  };

  // Restore the most recently removed column to its original index.
  // Used by the undo toast. Side effects on restore:
  //   1. The column reappears in `config.columns` (visible in the
  //      on-screen table at its original position).
  //   2. If the column was checked-off in the PDF picker before
  //      removal, that's preserved — but if hiddenInPdf was set by
  //      the user previously, we DON'T silently un-hide it on
  //      restore, since the user's "hide from PDF" intent is
  //      independent of column removal.
  //   3. `recentlyRestored` is flagged briefly so we can highlight
  //      the restored column header in green for ~2s, giving clear
  //      visual feedback about WHERE the column came back.
  const restoreColumn = async () => {
    if (!recentlyRemoved) return;
    const { column, index, timer } = recentlyRemoved;
    if (timer) clearTimeout(timer);
    const updated = [...config.columns];
    // Clamp index to current bounds in case other removes shrunk the
    // list below the original position.
    const insertAt = Math.min(index, updated.length);
    updated.splice(insertAt, 0, column);
    setConfig(prev => ({ ...prev, columns: updated }));
    setRecentlyRemoved(null);
    // Brief visual confirmation — the restored column header gets a
    // green ring for 2 seconds so the user can see exactly where the
    // column landed.
    setRecentlyRestored(column.id);
    setTimeout(() => setRecentlyRestored(null), 2000);
    try {
      await axios.put(
        `${API}/api/pipelines/${pipelineId}/status-report/config`,
        { columns: updated },
        { headers }
      );
    } catch (err) { console.error(err); }
  };

  // Add a default preset back to the report (used by the restore picker
  // in the Add Column dropdown). Re-uses the preset's original metadata
  // so the column behaves exactly as it did before removal.
  //
  // Insert position is computed from DEFAULT_PRESETS order: the
  // restored column lands BEFORE the first existing column whose
  // preset index is greater than its own. This means re-adding
  // "Location" with `[code, name, title, notes]` already present
  // inserts at index 3 (before notes), giving `[code, name, title,
  // location, notes]` — matching the canonical default order.
  // Custom (non-preset) columns are treated as having Infinity index
  // so they stay at the end of the list.
  const addPresetColumn = async (preset) => {
    if (config.columns.some(c => c.id === preset.id)) return; // already present
    const targetIdx = DEFAULT_PRESETS.findIndex(p => p.id === preset.id);
    const updated = [...config.columns];
    let insertAt = updated.length;
    for (let i = 0; i < updated.length; i++) {
      const colPresetIdx = DEFAULT_PRESETS.findIndex(p => p.id === updated[i].id);
      // Non-preset columns get treated as "after everything" so the
      // restored preset slots in among other presets in canonical
      // order, with custom columns naturally pushed to the end.
      const effectiveIdx = colPresetIdx === -1 ? Number.MAX_SAFE_INTEGER : colPresetIdx;
      if (effectiveIdx > targetIdx) {
        insertAt = i;
        break;
      }
    }
    updated.splice(insertAt, 0, preset);
    setConfig(prev => ({ ...prev, columns: updated }));
    setShowAddColumn(false);
    // Visual confirmation — green ring on the restored column for 2s.
    setRecentlyRestored(preset.id);
    setTimeout(() => setRecentlyRestored(null), 2000);
    try {
      await axios.put(
        `${API}/api/pipelines/${pipelineId}/status-report/config`,
        { columns: updated },
        { headers }
      );
    } catch (err) { console.error(err); }
  };

  // ---- Column drag-and-drop reorder ----
  // Same UX pattern as CandidateTable. Drag a column header onto
  // another header; the dragged column inserts at the target's
  // position. Locked columns (Name) can't be dragged AND can't be
  // dropped onto (they stay in place). Reorder persists to the
  // server on drop. Existing row drag-and-drop is untouched.
  const handleColumnDragStart = (e, columnId) => {
    setDraggedColumnId(columnId);
    e.dataTransfer.effectAllowed = 'move';
    // Required for Firefox to start the drag at all.
    try { e.dataTransfer.setData('text/plain', columnId); } catch {}
  };
  const handleColumnDragOver = (e, targetId) => {
    if (!draggedColumnId || draggedColumnId === targetId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverColumnId !== targetId) setDragOverColumnId(targetId);
  };
  const handleColumnDragLeave = () => {
    setDragOverColumnId(null);
  };
  const handleColumnDragEnd = () => {
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  };
  const handleColumnDrop = async (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    const sourceId = draggedColumnId;
    setDraggedColumnId(null);
    setDragOverColumnId(null);
    if (!sourceId || sourceId === targetId) return;
    const sourceIdx = config.columns.findIndex(c => c.id === sourceId);
    const targetIdx = config.columns.findIndex(c => c.id === targetId);
    if (sourceIdx < 0 || targetIdx < 0) return;
    if (config.columns[sourceIdx].locked) return;
    const updated = [...config.columns];
    const [moved] = updated.splice(sourceIdx, 1);
    const adjustedTarget = sourceIdx < targetIdx ? targetIdx - 1 : targetIdx;
    updated.splice(adjustedTarget, 0, moved);
    setConfig(prev => ({ ...prev, columns: updated }));
    try {
      await axios.put(
        `${API}/api/pipelines/${pipelineId}/status-report/config`,
        { columns: updated },
        { headers }
      );
    } catch (err) { console.error(err); }
  };

  // Add custom group
  const addGroup = async () => {
    if (!newGroupName.trim()) return;
    const id = 'group_' + Date.now();
    const newG = { id, name: newGroupName.trim(), color: newGroupColor, position: config.groups.length };
    const updated = [...config.groups, newG];
    setConfig(prev => ({ ...prev, groups: updated }));
    setShowAddGroup(false);
    setNewGroupName('');

    try {
      await axios.put(
        `${API}/api/pipelines/${pipelineId}/status-report/config`,
        { groups: updated },
        { headers }
      );
    } catch (err) { console.error(err); }
  };

  // Remove group (move candidates to ungrouped)
  const removeGroup = async (groupId) => {
    if (config.groups.length <= 1) return;
    const updated = config.groups.filter(g => g.id !== groupId);
    setConfig(prev => ({ ...prev, groups: updated }));

    // Move affected candidates to first remaining group
    const fallbackId = updated[0]?.id || '_ungrouped';
    const affected = candidates.filter(c => c.group_id === groupId);
    if (affected.length > 0) {
      setCandidates(prev => prev.map(c =>
        c.group_id === groupId ? { ...c, group_id: fallbackId } : c
      ));
      try {
        await axios.put(
          `${API}/api/pipelines/${pipelineId}/status-report/batch`,
          { updates: affected.map(c => ({ candidate_id: c.candidate_id, group_id: fallbackId, position: c.position })) },
          { headers }
        );
      } catch (err) { console.error(err); }
    }

    try {
      await axios.put(
        `${API}/api/pipelines/${pipelineId}/status-report/config`,
        { groups: updated },
        { headers }
      );
    } catch (err) { console.error(err); }
  };

  const GROUP_COLORS = [
    '#7C3AED', '#2563EB', '#059669', '#D97706', '#DC2626',
    '#6B7280', '#DB2777', '#0891B2', '#4F46E5', '#65A30D',
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!config) {
    return <div className="text-center py-16 text-gray-500">Failed to load status report</div>;
  }

  const groups = config.groups || [];
  const columns = config.columns || [];

  // === EXPORT FUNCTIONS ===
  const escapeHtml = (str) => String(str || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

  const exportCSV = () => {
    const rows = [];
    rows.push(['Group', ...columns.map(c => c.name)].map(v => `"${(v || '').replace(/"/g, '""')}"`).join(','));
    for (const group of groups) {
      for (const candidate of (grouped[group.id] || [])) {
        const row = [group.name, ...columns.map(col => getCellValue(candidate, col))];
        rows.push(row.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(','));
      }
    }
    for (const candidate of (grouped['_ungrouped'] || [])) {
      const row = ['Ungrouped', ...columns.map(col => getCellValue(candidate, col))];
      rows.push(row.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(','));
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `status-report-${pipelineName || pipelineId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const excludeCandidate = async (candidateId) => {
    try {
      const hdrs = { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' };
      await axios.put(`${API}/api/pipelines/${pipelineId}/status-report/candidate/${candidateId}/exclude`, { excluded: true }, { headers: hdrs });
      const removed = candidates.find(c => c.candidate_id === candidateId);
      setCandidates(prev => prev.filter(c => c.candidate_id !== candidateId));
      if (removed) {
        setExcludedCandidates(prev => [{ candidate_id: candidateId, name: removed._presets?.name || 'Unknown', excluded_at: new Date().toISOString() }, ...prev]);
        setExcludedCount(prev => prev + 1);
      }
    } catch (e) { console.error('Exclude failed:', e); }
  };

  const restoreCandidate = async (candidateId) => {
    try {
      const hdrs = { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' };
      await axios.put(`${API}/api/pipelines/${pipelineId}/status-report/candidate/${candidateId}/exclude`, { excluded: false }, { headers: hdrs });
      setExcludedCandidates(prev => prev.filter(c => c.candidate_id !== candidateId));
      setExcludedCount(prev => Math.max(0, prev - 1));
      // Reload to get full candidate data back
      const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
      const res = await axios.get(`${API}/api/pipelines/${pipelineId}/status-report`, { headers });
      setCandidates(res.data.candidates);
    } catch (e) { console.error('Restore failed:', e); }
  };

  const loadExcluded = async () => {
    try {
      const hdrs = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
      const res = await axios.get(`${API}/api/pipelines/${pipelineId}/status-report/excluded`, { headers: hdrs });
      setExcludedCandidates(res.data);
    } catch (e) { setExcludedCandidates([]); }
  };

  const generateShareLinks = async () => {
    setGeneratingLinks(true);
    try {
      // Collect all unique file IDs from candidates
      const allFileIds = [];
      candidates.forEach(c => (c.presentation_files || []).forEach(f => { if (!allFileIds.includes(f.id)) allFileIds.push(f.id); }));
      if (allFileIds.length === 0) { alert('No presentation files to share'); setGeneratingLinks(false); return; }

      const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' };
      const res = await axios.post(`${API}/api/pipelines/${pipelineId}/share-links`, {
        file_ids: allFileIds,
        expires_hours: shareConfig.expires_hours || null,
        password: shareConfig.password || null
      }, { headers });

      const linkMap = {};
      (res.data.links || []).forEach(l => { linkMap[l.file_id] = l.url; });
      setShareLinks(linkMap);
      setShowShareModal(false);
      // Auto-export PDF with share links
      setTimeout(() => exportPDF(linkMap), 100);
    } catch (e) { console.error('Share link error:', e); alert('Failed to generate share links'); }
    finally { setGeneratingLinks(false); }
  };

  const exportPDF = (shareLinkMap) => {
    const links = shareLinkMap || shareLinks;
    const w = window.open('', '_blank');
    if (!w) { alert('Please allow popups to export PDF.'); return; }
    console.log('[PDF] Generating v3 — logo:', !!firmLogo, 'firmName:', firmName, 'candidates:', candidates.length, 'orientation:', pdfOrientation, 'hidden:', hiddenInPdf.size);
    const dateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    // Columns to include in the PDF — user can hide columns via the column
    // picker without removing them from the on-screen table.
    const pdfColumns = columns.filter(c => !hiddenInPdf.has(c.id));

    // Proportional column widths from React state applied via <colgroup>.
    // Respecting the user-adjusted widths means narrow columns stay narrow
    // in the PDF and wide columns stay wide — instead of the browser's
    // auto-sizing letting a few wide cells dominate. table-layout:fixed
    // (set in CSS) makes the percentages stick.
    const totalWidthPx = pdfColumns.reduce((sum, c) => sum + (parseInt(c.width, 10) || 100), 0) || 1;
    const colgroupHtml = `<colgroup>${pdfColumns.map(c =>
      `<col style="width:${(((parseInt(c.width, 10) || 100) / totalWidthPx) * 100).toFixed(2)}%" />`
    ).join('')}</colgroup>`;

    const isLandscape = pdfOrientation === 'landscape';
    let html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Status Report — ${escapeHtml(pipelineName || 'Pipeline')}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:'Segoe UI',Roboto,-apple-system,sans-serif;color:#1a1a1a;font-size:9px}

        /* Header */
        .header{padding:24px 32px 16px;border-bottom:3px solid #6366f1}
        .header-row{display:flex;justify-content:space-between;align-items:flex-start}
        .header h1{font-size:20px;font-weight:700;color:#111827;letter-spacing:-0.5px}
        .header .pipeline-name{font-size:13px;color:#6366f1;font-weight:500;margin-top:2px}
        .header .meta{text-align:right;font-size:9px;color:#6b7280;line-height:1.5}
        .header .meta strong{color:#374151}

        /* Content */
        .content{padding:16px 32px 32px}

        /* Groups */
        .grp{margin-bottom:20px;page-break-inside:avoid}
        .gh{font-size:11px;font-weight:700;padding:6px 10px;border-radius:6px;margin-bottom:5px;color:#fff;display:flex;justify-content:space-between;align-items:center}
        .gh .cnt{font-weight:400;font-size:9px;opacity:0.85}

        /* Table — fixed layout respects <colgroup> widths; tighter than
           the previous CSS so more columns fit per row. word-break +
           overflow-wrap + hyphens together stop long single tokens from
           overflowing the cell and breaking the layout. */
        table{width:100%;border-collapse:collapse;margin-bottom:4px;table-layout:fixed}
        th{text-align:left;padding:4px 6px;font-size:7px;text-transform:uppercase;letter-spacing:.6px;color:#6b7280;border-bottom:2px solid #e5e7eb;font-weight:600;background:#f9fafb;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        td{padding:4px 6px;border-bottom:1px solid #f3f4f6;font-size:9px;vertical-align:top;word-break:break-word;overflow-wrap:anywhere;hyphens:auto}
        tr:nth-child(even){background:#fafafa}
        tr:last-child td{border-bottom:none}
        .nm{font-weight:600;color:#111827}
        .nm img,.nm span{vertical-align:middle}
        .nm a{color:#4f46e5;text-decoration:none;font-weight:600}
        .nm a:hover{text-decoration:underline}
        .extra-file{font-size:7px;color:#6366f1;font-weight:500;margin-left:3px;padding:1px 3px;background:#f0f0ff;border-radius:3px;text-decoration:none;white-space:nowrap}
        .extra-file:hover{text-decoration:underline}
        .em{color:#d1d5db}

        /* Footer */
        .footer{padding:12px 32px;border-top:1px solid #e5e7eb;font-size:7px;color:#9ca3af;display:flex;justify-content:space-between}

        @media print{
          body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
          .header{padding:16px 20px 12px}
          .content{padding:12px 20px 20px}
          .footer{padding:10px 20px}
          .grp{page-break-inside:avoid}
        }
        @page{size:${isLandscape ? 'landscape' : 'portrait'};margin:0.3cm}
      </style></head><body>`;
    
    // Header
    const logoHtml = firmLogo 
      ? `<img src="${firmLogo}" style="max-height:40px;max-width:160px;object-fit:contain" />`
      : `<strong>${escapeHtml(firmName || 'Banyak Group')}</strong>`;
    html += `<div class="header"><div class="header-row">`;
    html += `<div><h1>Status Report</h1><div class="pipeline-name">${escapeHtml(pipelineName || '')}</div></div>`;
    html += `<div class="meta">${logoHtml}<br/>${dateStr}<br/>${candidates.length} candidate${candidates.length !== 1 ? 's' : ''}</div>`;
    html += `</div></div>`;
    
    // Content
    html += `<div class="content">`;
    for (const group of groups) {
      const gc = grouped[group.id] || [];
      if (gc.length === 0) continue;
      html += `<div class="grp"><div class="gh" style="background:${group.color}"><span>${escapeHtml(group.name)}</span><span class="cnt">${gc.length}</span></div>`;
      html += `<table>${colgroupHtml}<thead><tr>${pdfColumns.map(c => `<th>${escapeHtml(c.name)}</th>`).join('')}</tr></thead><tbody>`;
      for (const c of gc) {
        html += `<tr>${pdfColumns.map(col => {
          const v = getCellValue(c, col);
          if (col.presetKey === 'name') {
            const files = c.presentation_files || [];
            const appBase = window.location.origin;
            const pic = c.profile_picture_url 
              ? `<img src="${c.profile_picture_url}" style="width:18px;height:18px;border-radius:50%;object-fit:cover;vertical-align:middle;margin-right:4px" onerror="this.style.display='none'" />`
              : `<span style="display:inline-block;width:18px;height:18px;border-radius:50%;background:#e5e7eb;text-align:center;line-height:18px;font-size:8px;font-weight:600;color:#6b7280;vertical-align:middle;margin-right:4px">${(v || '?').charAt(0).toUpperCase()}</span>`;
            const nameHtml = files.length > 0
              ? `<a href="${links[files[0].id] || `${appBase}/file/${files[0].id}`}" target="_blank">${escapeHtml(v || '—')}</a>`
              : escapeHtml(v || '—');
            const extraFiles = files.slice(1).map((f, i) => 
              `<a href="${links[f.id] || `${appBase}/file/${f.id}`}" target="_blank" class="extra-file" title="${f.filename}">File ${i + 2}</a>`
            ).join(' ');
            return `<td class="nm">${pic}${nameHtml}${extraFiles ? ' ' + extraFiles : ''}</td>`;
          }
          return `<td>${v ? escapeHtml(v) : '<span class="em">—</span>'}</td>`;
        }).join('')}</tr>`;
      }
      html += `</tbody></table></div>`;
    }
    html += `</div>`;
    
    // Footer
    html += `<div class="footer"><span>Confidential — ${escapeHtml(firmName || 'Banyak Group')}</span><span>Generated ${dateStr}</span></div>`;
    html += `</body></html>`;
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="bg-white border-b px-4 py-2 flex items-center gap-2 flex-shrink-0">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mr-2">Status Report</span>

        {/* Add Column */}
        {!readOnly && (
        <div className="relative">
          <button
            onClick={() => { setShowAddColumn(!showAddColumn); setShowAddGroup(false); }}
            className="px-2 py-1 text-xs border border-gray-300 rounded text-gray-700 hover:bg-gray-50 flex items-center gap-1"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Column
          </button>
          {showAddColumn && (() => {
            // Missing default presets — anything in DEFAULT_PRESETS not
            // currently in config.columns. Filtered by permission so we
            // don't offer e.g. the Comp column to a user without view_
            // compensation (they couldn't see the data anyway, and the
            // server would strip it).
            const presentIds = new Set(config.columns.map(c => c.id));
            const missingPresets = DEFAULT_PRESETS.filter(p =>
              !presentIds.has(p.id) &&
              (!p.permission || hasPermission(p.permission))
            );
            return (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowAddColumn(false)} />
              <div className="absolute left-0 top-full mt-1 w-60 bg-white border rounded-lg shadow-lg z-50 py-1">
                {missingPresets.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[10px] text-gray-500 uppercase tracking-wide border-b border-gray-100">
                      Restore default
                    </div>
                    {missingPresets.map(p => (
                      <button key={p.id}
                        onClick={() => addPresetColumn(p)}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50 text-gray-700 flex items-center gap-2"
                        title={`Re-add the ${p.name} column`}>
                        <svg className="w-3 h-3 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span className="truncate">{p.name}</span>
                      </button>
                    ))}
                    <div className="border-t border-gray-100 mt-1 pt-1" />
                  </>
                )}
                <div className="px-3 py-1.5 text-[10px] text-gray-500 uppercase tracking-wide">
                  {missingPresets.length > 0 ? 'Or add a new column' : 'Add a new column'}
                </div>
                <div className="px-3 pb-2 pt-1">
                  <input
                    type="text"
                    value={newColumnName}
                    onChange={(e) => setNewColumnName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addColumn(); if (e.key === 'Escape') setShowAddColumn(false); }}
                    placeholder="Column name..."
                    className="w-full px-2 py-1.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 mb-2"
                    autoFocus
                  />
                  <button onClick={addColumn} disabled={!newColumnName.trim()}
                    className="w-full px-2 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                    Add Column
                  </button>
                </div>
              </div>
            </>
            );
          })()}
        </div>
        )}

        {/* Add Group */}
        {!readOnly && (
        <div className="relative">
          <button
            onClick={() => { setShowAddGroup(!showAddGroup); setShowAddColumn(false); }}
            className="px-2 py-1 text-xs border border-gray-300 rounded text-gray-700 hover:bg-gray-50 flex items-center gap-1"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Group
          </button>
          {showAddGroup && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowAddGroup(false)} />
              <div className="absolute left-0 top-full mt-1 w-56 bg-white border rounded-lg shadow-lg z-50 p-3">
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addGroup(); if (e.key === 'Escape') setShowAddGroup(false); }}
                  placeholder="Group name..."
                  className="w-full px-2 py-1.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 mb-2"
                  autoFocus
                />
                <div className="flex gap-1 mb-2">
                  {GROUP_COLORS.map(c => (
                    <button key={c} onClick={() => setNewGroupColor(c)}
                      className={`w-5 h-5 rounded-full border-2 ${newGroupColor === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <button onClick={addGroup} disabled={!newGroupName.trim()}
                  className="w-full px-2 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                  Add Group
                </button>
              </div>
            </>
          )}
        </div>
        )}

        <div className="flex-1" />

        <button onClick={exportCSV}
          className="px-2 py-1 text-xs border border-gray-300 rounded text-gray-700 hover:bg-gray-50 flex items-center gap-1"
          title="Export as CSV">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          CSV
        </button>
        {/* PDF export group: main button + orientation toggle + column
            picker. Three sub-buttons share identical padding/height so
            the pill reads as one unit. The column-picker icon uses a
            stacked-bars motif (not a hamburger) so it actually looks
            like column selection. */}
        <div className="flex items-stretch border border-gray-300 rounded overflow-hidden h-7">
          <button onClick={() => exportPDF()}
            className="px-2.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"
            title={`Print / Save as PDF (${pdfOrientation}${hiddenInPdf.size > 0 ? `, ${columns.length - hiddenInPdf.size}/${columns.length} columns` : ''})`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
            PDF
          </button>
          {/* Orientation toggle — clicking flips the value; the icon
              rotates so it always shows the CURRENT setting at a glance. */}
          <button onClick={() => setPdfOrientation(pdfOrientation === 'landscape' ? 'portrait' : 'landscape')}
            className="px-2 text-xs text-gray-500 hover:bg-gray-50 border-l border-gray-300 flex items-center"
            title={`Orientation: ${pdfOrientation}. Click to switch.`}>
            {pdfOrientation === 'landscape' ? (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="1.5" strokeWidth="1.8" /></svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="6" y="3" width="12" height="18" rx="1.5" strokeWidth="1.8" /></svg>
            )}
          </button>
          {/* Column picker — anchored dropdown showing every column with
              a checkbox. The locked Name column is non-removable.
              Icon is stacked vertical bars (column motif). When columns
              are hidden, the whole button gets an indigo tint instead
              of squeezing a numeric badge inline — cleaner read. */}
          <button onClick={(e) => {
              if (columnPickerAnchor) { setColumnPickerAnchor(null); return; }
              const rect = e.currentTarget.getBoundingClientRect();
              setColumnPickerAnchor({ left: rect.right - 220, top: rect.bottom + 4 });
            }}
            className={`px-2 text-xs border-l border-gray-300 flex items-center transition-colors ${hiddenInPdf.size > 0 ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100' : 'text-gray-500 hover:bg-gray-50'}`}
            title={`Choose columns for PDF${hiddenInPdf.size > 0 ? ` (${hiddenInPdf.size} hidden of ${columns.length})` : ''}`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="4" y="5" width="3.5" height="14" rx="0.75" strokeWidth="1.6" />
              <rect x="10.25" y="5" width="3.5" height="14" rx="0.75" strokeWidth="1.6" />
              <rect x="16.5" y="5" width="3.5" height="14" rx="0.75" strokeWidth="1.6" />
            </svg>
          </button>
        </div>
        {columnPickerAnchor && createPortal(
          <>
            {/* Backdrop to catch outside clicks. */}
            <div
              onClick={() => setColumnPickerAnchor(null)}
              style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
            {/* Inline styles + portal to document.body to escape
                transformed-ancestor traps. This picker is for hiding
                CURRENTLY-present columns from PDF export only. To
                re-add a previously-removed column, use the "Column"
                toolbar button on the left — its dropdown contains the
                Restore-default list of any missing presets. */}
            <div
              style={{
                position: 'fixed',
                left: Math.max(8, columnPickerAnchor.left),
                top: columnPickerAnchor.top,
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                zIndex: 9999,
                minWidth: '220px',
                padding: '4px 0',
                maxHeight: '288px',
                overflowY: 'auto',
              }}>
              <div style={{ padding: '6px 12px', fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>PDF Columns</span>
                <button onClick={() => setHiddenInPdf(new Set())}
                  style={{ fontSize: '10px', color: '#4f46e5', background: 'transparent', border: 'none', cursor: 'pointer', textTransform: 'none', letterSpacing: 'normal', opacity: hiddenInPdf.size === 0 ? 0.4 : 1 }}
                  disabled={hiddenInPdf.size === 0}>
                  Show all
                </button>
              </div>
              {columns.map(col => {
                const isHidden = hiddenInPdf.has(col.id);
                const isLocked = col.locked;
                return (
                  <label key={col.id}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', fontSize: '12px', cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.6 : 1 }}
                    onMouseEnter={(e) => { if (!isLocked) e.currentTarget.style.background = '#eef2ff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                    <input type="checkbox"
                      checked={!isHidden}
                      disabled={isLocked}
                      onChange={() => {
                        if (isLocked) return;
                        setHiddenInPdf(prev => {
                          const next = new Set(prev);
                          if (next.has(col.id)) next.delete(col.id); else next.add(col.id);
                          return next;
                        });
                      }}
                      style={{ width: '12px', height: '12px' }}
                    />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.name}</span>
                    {isLocked && <span style={{ fontSize: '9px', color: '#9ca3af' }}>required</span>}
                  </label>
                );
              })}
            </div>
          </>,
          document.body
        )}
        <button onClick={() => setShowShareModal(true)}
          className="px-2 py-1 text-xs border border-blue-300 bg-blue-50 rounded text-blue-700 hover:bg-blue-100 flex items-center gap-1"
          title="Export PDF with public share links">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
          Share
        </button>

        <span className="text-[11px] text-gray-400 tabular-nums">
          {candidates.length} candidates · {groups.length} groups
        </span>
        {!readOnly && (excludedCount > 0 || showExcluded) && (
          <button onClick={() => setShowExcluded(!showExcluded)}
            className={`text-[10px] px-2 py-0.5 rounded transition-colors ${showExcluded ? 'bg-amber-100 text-amber-700' : 'text-gray-400 hover:text-gray-600'}`}>
            {showExcluded ? 'Hide' : 'Show'} removed{(excludedCandidates.length || excludedCount) > 0 ? ` (${excludedCandidates.length || excludedCount})` : ''}
          </button>
        )}
      </div>

      {/* Excluded candidates */}
      {showExcluded && excludedCandidates.length > 0 && (
        <div className="mx-4 mt-2 mb-1 border border-amber-200 bg-amber-50 rounded-lg p-3">
          <div className="text-xs font-medium text-amber-800 mb-2">Removed from Status Report</div>
          <div className="space-y-1">
            {excludedCandidates.map(c => (
              <div key={c.candidate_id} className="flex items-center justify-between py-1 px-2 bg-white rounded border border-amber-100">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-700">{c.name}</span>
                  <span className="text-[9px] text-gray-400">{c.excluded_at ? new Date(c.excluded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                </div>
                <button onClick={() => restoreCandidate(c.candidate_id)}
                  className="text-[10px] text-blue-600 hover:text-blue-800 font-medium px-2 py-0.5 hover:bg-blue-50 rounded">
                  Restore
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 overflow-auto bg-gray-50 p-4">
        {groups.map(group => {
          const groupCandidates = grouped[group.id] || [];
          const isCollapsed = collapsedGroups.has(group.id);
          const isDragOver = dragOverGroup === group.id;

          return (
            <div
              key={group.id}
              className={`mb-4 rounded-lg border bg-white shadow-sm overflow-hidden transition-shadow ${
                isDragOver ? 'ring-2 ring-blue-400 shadow-md' : ''
              }`}
              onDragOver={(e) => handleDragOver(e, group.id)}
              onDrop={(e) => handleDrop(e, group.id)}
              onDragLeave={() => setDragOverGroup(null)}
            >
              {/* Group Header */}
              <div
                className="flex items-center gap-2 px-4 py-2.5 cursor-pointer select-none"
                style={{ backgroundColor: group.color + '12', borderLeft: `4px solid ${group.color}` }}
                onClick={() => toggleGroup(group.id)}
              >
                <svg className={`w-3.5 h-3.5 text-gray-500 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-sm font-semibold" style={{ color: group.color }}>{group.name}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                  style={{ backgroundColor: group.color + '20', color: group.color }}>
                  {groupCandidates.length}
                </span>
                <div className="flex-1" />
                {!readOnly && (
                <button
                  onClick={(e) => { e.stopPropagation(); if (confirm(`Remove group "${group.name}"?`)) removeGroup(group.id); }}
                  className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100"
                  title="Remove group"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                )}
              </div>

              {/* Table */}
              {!isCollapsed && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="w-8 px-1"></th>{/* drag handle */}
                        {columns.map(col => (
                          // `group` enables the X button below to show on
                          // hover of the whole header (group-hover) — the
                          // previous behavior only showed it when hovering
                          // the invisible X itself, which was nearly
                          // undiscoverable.
                          // `position: relative` anchors the right-edge
                          // resize handle, which lives outside the flex row
                          // so it can sit flush against the column border.
                          // Explicit `bg-gray-50` on each th: with
                          // position:relative on table cells, some browsers
                          // create a new stacking context that breaks the
                          // tr-level background pass-through, leaving the
                          // X-button area looking white. Setting it on the
                          // th itself makes the gray reliable.
                          <th key={col.id}
                            // Locked columns (Name) can't be dragged.
                            // The native HTML5 draggable attribute is
                            // toggled per-column.
                            draggable={!col.locked && !readOnly}
                            onDragStart={(e) => handleColumnDragStart(e, col.id)}
                            onDragOver={(e) => handleColumnDragOver(e, col.id)}
                            onDragLeave={handleColumnDragLeave}
                            onDragEnd={handleColumnDragEnd}
                            onDrop={(e) => handleColumnDrop(e, col.id)}
                            className={`group relative bg-gray-50 px-2 py-1.5 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap select-none transition-all ${recentlyRestored === col.id ? 'ring-2 ring-emerald-400 ring-inset' : ''} ${draggedColumnId === col.id ? 'opacity-40' : ''} ${dragOverColumnId === col.id && draggedColumnId !== col.id ? 'border-l-2 border-blue-500' : ''} ${!col.locked && !readOnly ? 'cursor-grab active:cursor-grabbing' : ''}`}
                            style={{ width: col.width, minWidth: col.width }}>
                            <div className="flex items-center gap-1">
                              <span className="truncate">{col.name}</span>
                              {!col.locked && !readOnly && (
                                // `ml-auto` pushes the X to the right edge
                                // of the cell instead of butting up against
                                // the column name. `bg-transparent` overrides
                                // the user-agent default button background
                                // so the gray header shows through cleanly.
                                <button onClick={() => removeColumn(col.id)}
                                  className="opacity-0 group-hover:opacity-100 ml-auto text-gray-400 hover:text-red-500 flex-shrink-0 transition-opacity bg-transparent"
                                  title="Remove column">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {/* Resize handle — 4px wide strip flush against
                                the column's right edge. Lights up on hover
                                and during an active drag. Clicking on it
                                starts a global mousemove that updates
                                col.width in real time; mouseup persists. */}
                            {!readOnly && (
                              <div
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  const startX = e.clientX;
                                  const startW = parseInt(col.width, 10) || 100;
                                  setResizingCol({ id: col.id, startX, startW });
                                }}
                                className={`absolute top-0 right-0 h-full w-1 cursor-col-resize transition-colors ${resizingCol?.id === col.id ? 'bg-blue-400' : 'bg-transparent hover:bg-blue-300'}`}
                                title="Drag to resize"
                              />
                            )}
                          </th>
                        ))}
                        {/* Placeholder th matching the per-row X button td
                            below. Without this, the data rows have N+1
                            cells but the thead has N — the extra column
                            position renders without a header cell, leaving
                            a strip where the parent tr's bg-gray-50 can't
                            cover. Adding the placeholder closes that gap
                            and keeps thead/tbody column counts aligned. */}
                        {!readOnly && <th className="w-6 px-1 bg-gray-50"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {groupCandidates.length === 0 ? (
                        <tr>
                          <td colSpan={columns.length + 1} className="px-4 py-6 text-center text-gray-400 text-xs">
                            Drag candidates here
                          </td>
                        </tr>
                      ) : (
                        groupCandidates.map(candidate => (
                          <tr
                            key={candidate.candidate_id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, candidate)}
                            onDragEnd={handleDragEnd}
                            className={`group hover:bg-blue-50/40 transition-colors ${
                              dragCandidate?.candidate_id === candidate.candidate_id ? 'opacity-40' : ''
                            }`}
                          >
                            {/* Drag handle */}
                            <td className="w-8 px-1 text-center cursor-grab active:cursor-grabbing">
                              <svg className="w-3.5 h-3.5 text-gray-300 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
                              </svg>
                            </td>
                            {columns.map(col => {
                              const value = getCellValue(candidate, col);
                              const isEditing = editingCell?.candidateId === candidate.candidate_id
                                && editingCell?.columnId === col.id;
                              const isEditable = col.type !== 'preset' || (col.type === 'preset' && !col.locked);
                              const isNameCol = col.presetKey === 'name';

                              // Name column: editable like other cells,
                              // but with avatar + file links overlay when
                              // not editing. The condition `!editingCell`
                              // used to hide the avatar across all name
                              // rows whenever ANY cell was being edited;
                              // `!isEditing` only hides it for the row
                              // currently being edited.
                              if (isNameCol) {
                                const files = candidate.presentation_files || [];
                                const downloadFile = (e, fileId, filename) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // Open the file in a new tab (like the PDF export)
                                  // rather than force-downloading it. Open the tab
                                  // synchronously on click to avoid popup blockers,
                                  // then point it at the blob URL once it resolves.
                                  const w = window.open('', '_blank');
                                  fetch(`/api/cards/presentation-files/${fileId}/download`, {
                                    headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
                                  }).then(r => r.blob()).then(blob => {
                                    const url = URL.createObjectURL(blob);
                                    if (w) { w.location = url; }
                                    else { const a = document.createElement('a'); a.href = url; a.download = filename || 'file'; a.click(); }
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                  }).catch(err => { console.error('Open failed:', err); if (w) w.close(); });
                                };
                                if (isEditing) {
                                  return (
                                    <td key={col.id} style={{ width: col.width, minWidth: col.width }}
                                      className="px-2 py-1.5">
                                      <input
                                        ref={editInputRef}
                                        type="text"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onBlur={commitEdit}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') commitEdit();
                                          if (e.key === 'Escape') { setEditingCell(null); setEditValue(''); }
                                          e.stopPropagation();
                                        }}
                                        className="w-full px-1 py-0 text-xs border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                                      />
                                    </td>
                                  );
                                }
                                return (
                                  <td key={col.id} style={{ width: col.width, minWidth: col.width }}
                                    className="px-2 py-1.5 font-semibold text-gray-900 cursor-text"
                                    onClick={() => startEdit(candidate.candidate_id, col.id, value)}>
                                    <div className="flex items-center gap-1.5">
                                      {candidate.profile_picture_url ? (
                                        <img src={candidate.profile_picture_url} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                                      ) : (
                                        <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-[8px] font-medium text-gray-500 flex-shrink-0">
                                          {(value || '?').charAt(0).toUpperCase()}
                                        </div>
                                      )}
                                      <span className="truncate">{value || '—'}</span>
                                      {files.length > 0 && (
                                        <div className="flex-shrink-0">
                                          <button onClick={(e) => {
                                            e.stopPropagation();
                                            if (files.length === 1) { downloadFile(e, files[0].id, files[0].filename); return; }
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setOpenFileDropdown(openFileDropdown?.candidateId === candidate.candidate_id ? null : { candidateId: candidate.candidate_id, files, rect: { left: rect.left, top: rect.bottom + 4 } });
                                          }}
                                            className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200"
                                            title={files[0].filename}>
                                            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                            {files.length > 1 ? files.length : ''}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                );
                              }

                              return (
                                <td key={col.id}
                                  className={`px-2 py-1.5 ${isEditable ? 'cursor-text' : ''}`}
                                  style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                                  onClick={() => isEditable && startEdit(candidate.candidate_id, col.id, value)}
                                >
                                  {isEditing ? (
                                    <div className="relative">
                                      <input
                                        ref={editInputRef}
                                        type="text"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onBlur={commitEdit}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') commitEdit();
                                          if (e.key === 'Escape') { setEditingCell(null); setEditValue(''); }
                                          e.stopPropagation();
                                        }}
                                        className="w-full px-1 py-0 pr-6 text-xs border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                                      />
                                      {hasOverride(candidate, col) && (
                                        <button
                                          // Clear the manual override and let the live preset value show.
                                          onMouseDown={(e) => {
                                            // mousedown beats the input's onBlur so the click fires before commitEdit
                                            e.preventDefault();
                                            e.stopPropagation();
                                            saveCell(candidate.candidate_id, col.id, '');
                                            setEditingCell(null);
                                            setEditValue('');
                                          }}
                                          title="Reset to live data (remove manual override)"
                                          className="absolute right-0.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="truncate text-gray-700" title={value}>
                                      {col.presetKey === 'name' ? (
                                        <div className="flex items-center gap-1.5">
                                          <span className="font-medium text-gray-900">{value}</span>
                                          {candidate.client_status === 'withdrew' && candidate.group_id === 'withdrawn' && (
                                            <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-purple-100 text-purple-700 flex-shrink-0 whitespace-nowrap">{candidate.rejected_at_stage_name ? `at ${candidate.rejected_at_stage_name}` : 'Withdrew'}</span>
                                          )}
                                          {candidate.rejected_at_stage_name && candidate.group_id === 'disqualified' && (
                                            <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-orange-100 text-orange-700 flex-shrink-0 whitespace-nowrap">at {candidate.rejected_at_stage_name}</span>
                                          )}
                                        </div>
                                      ) : (
                                        value || <span className="text-gray-300">—</span>
                                      )}
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                            {!readOnly && (
                              <td className="w-6 px-1">
                                <button onClick={() => excludeCandidate(candidate.candidate_id)}
                                  className="p-0.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Remove from status report">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {/* Ungrouped candidates */}
        {grouped['_ungrouped']?.length > 0 && (
          <div className="mb-4 rounded-lg border bg-white shadow-sm overflow-hidden border-dashed"
            onDragOver={(e) => handleDragOver(e, '_ungrouped')}
            onDrop={(e) => handleDrop(e, '_ungrouped')}
            onDragLeave={() => setDragOverGroup(null)}
          >
            <div className="px-4 py-2 bg-gray-50 border-b">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Ungrouped ({grouped['_ungrouped'].length})</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <tbody className="divide-y divide-gray-100">
                  {grouped['_ungrouped'].map(candidate => (
                    <tr key={candidate.candidate_id} draggable
                      onDragStart={(e) => handleDragStart(e, candidate)}
                      onDragEnd={handleDragEnd}
                      className="hover:bg-gray-50">
                      <td className="w-8 px-1 text-center cursor-grab">
                        <svg className="w-3.5 h-3.5 text-gray-300 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
                        </svg>
                      </td>
                      {columns.map(col => (
                        <td key={col.id} className="px-2 py-1.5 truncate text-gray-600"
                          style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}>
                          {getCellValue(candidate, col) || '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {candidates.length === 0 && (
          <div className="text-center py-16">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
            </svg>
            <p className="text-sm text-gray-500">No candidates on pipeline yet</p>
            <p className="text-xs text-gray-400 mt-1">Add candidates to the pipeline from the Search Pool tab</p>
          </div>
        )}
      </div>

      {/* Share Link Modal */}
      {showShareModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowShareModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowShareModal(false)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Share Status Report</h3>
              <p className="text-xs text-gray-500 mb-4">Generate public links for presentation files. Recipients won't need an account.</p>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Link Expiry</label>
                  <select value={shareConfig.expires_hours || ''}
                    onChange={(e) => setShareConfig(prev => ({ ...prev, expires_hours: e.target.value ? parseInt(e.target.value) : null }))}
                    className="w-full px-2.5 py-1.5 text-sm border rounded-lg">
                    <option value="24">24 hours</option>
                    <option value="72">3 days</option>
                    <option value="168">1 week</option>
                    <option value="720">30 days</option>
                    <option value="">Never expire</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Password (optional)</label>
                  <input type="text" value={shareConfig.password}
                    onChange={(e) => setShareConfig(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Leave blank for no password"
                    className="w-full px-2.5 py-1.5 text-sm border rounded-lg" />
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4">
                <button onClick={() => setShowShareModal(false)} className="flex-1 px-3 py-2 text-xs border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={generateShareLinks} disabled={generatingLinks}
                  className="flex-1 px-3 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                  {generatingLinks ? 'Generating...' : 'Generate & Export PDF'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Fixed-position file dropdown */}
      {openFileDropdown && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpenFileDropdown(null)} />
          <div className="fixed bg-white border rounded-lg shadow-xl z-50 min-w-[200px] py-1"
            style={{ left: openFileDropdown.rect.left, top: openFileDropdown.rect.top }}>
            {openFileDropdown.files.map(f => (
              <button key={f.id} onClick={(e) => {
                e.stopPropagation();
                // Open in a new tab rather than force-download (mirrors PDF export).
                const w = window.open('', '_blank');
                fetch(`/api/cards/presentation-files/${f.id}/download`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
                }).then(r => r.blob()).then(blob => {
                  const url = URL.createObjectURL(blob);
                  if (w) { w.location = url; }
                  else { const a = document.createElement('a'); a.href = url; a.download = f.filename || 'file'; a.click(); }
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                }).catch(err => { console.error('Open failed:', err); if (w) w.close(); });
                setOpenFileDropdown(null);
              }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-indigo-50 text-left">
                <svg className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                <span className="truncate">{f.filename}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Undo toast — appears at the bottom-center of the viewport
          immediately after a column is removed. Stays for 8 seconds or
          until the user clicks Undo / Dismiss.
          Rendered through createPortal into document.body so it
          escapes any transformed ancestor that would otherwise trap
          its `position: fixed` to a local containing block (the
          previous in-place render was getting hidden by exactly this).
          Inline styles instead of `animate-in` Tailwind classes
          because the `tailwindcss-animate` plugin isn't installed in
          this project — relying on those classes left the toast
          invisible (no opacity rule, no keyframe). */}
      {recentlyRemoved && createPortal(
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: '#111827',
            color: 'white',
            fontSize: '12px',
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 16px',
          }}>
          <span>
            Removed column <span style={{ fontWeight: 600 }}>"{recentlyRemoved.column.name}"</span>
          </span>
          <button
            onClick={restoreColumn}
            style={{ color: '#6ee7b7', fontWeight: 600, background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'none' }}
            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}>
            Undo
          </button>
          <button
            onClick={() => {
              if (recentlyRemoved?.timer) clearTimeout(recentlyRemoved.timer);
              setRecentlyRemoved(null);
            }}
            style={{ color: '#9ca3af', background: 'transparent', border: 'none', cursor: 'pointer', paddingLeft: '8px', borderLeft: '1px solid #374151', display: 'flex', alignItems: 'center' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'white'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
            title="Dismiss">
            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>,
        document.body
      )}
      {/* Disqualify / Withdraw reason prompt — shown when a candidate is
          dragged into the Disqualified or Withdrawn group. Capturing the reason
          here (and letting the server apply the real status) keeps the status
          report in lockstep with the CPP and interview stages. */}
      {terminalDrop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !terminalSaving && (setTerminalDrop(null), setTerminalReason(''))}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2.5 h-2.5 rounded-full ${terminalDrop.groupId === 'withdrawn' ? 'bg-purple-500' : 'bg-red-500'}`} />
              <h3 className="text-sm font-semibold text-gray-900">
                {terminalDrop.groupId === 'withdrawn' ? 'Mark as Withdrawn' : 'Disqualify'} — {terminalDrop.name}
              </h3>
            </div>
            <p className="text-[11px] text-gray-500 mb-2">
              This updates the candidate's status everywhere (pipeline, interview stages, search pool), not just this report.
            </p>
            <textarea
              value={terminalReason}
              onChange={(e) => setTerminalReason(e.target.value)}
              autoFocus
              rows={3}
              placeholder={terminalDrop.groupId === 'withdrawn' ? 'Reason for withdrawal (optional)…' : 'Reason for disqualification (optional)…'}
              className="w-full text-xs px-2.5 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-400 resize-none"
            />
            <div className="flex items-center justify-end gap-2 mt-3">
              <button onClick={() => { setTerminalDrop(null); setTerminalReason(''); }} disabled={terminalSaving}
                className="text-xs px-3 py-1.5 text-gray-600 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={commitTerminalDrop} disabled={terminalSaving}
                className={`text-xs px-3 py-1.5 text-white rounded font-medium disabled:opacity-50 ${terminalDrop.groupId === 'withdrawn' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'}`}>
                {terminalSaving ? 'Saving…' : (terminalDrop.groupId === 'withdrawn' ? 'Confirm Withdrawal' : 'Confirm Disqualify')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusReport;
